/**
 * Press-to-start gate (sc-2823).
 *
 * Sits between cartridge load and cartridge gameplay. Real arcade cabinets
 * never launch into active gameplay — there's always a "PRESS START" beat
 * after coin insert. This gate gives every cartridge that beat without
 * requiring per-cartridge code.
 *
 * Visual: cabinet's vaporwave gradient background, cartridge title in the
 * cartridge's accent color, blinking "PRESS START" yellow, palette dots
 * underneath as a subtle nod to the marquee tint reservation.
 *
 * Input: any of start / fireA / coin / pointerdown releases the gate.
 * A short lockout (~300ms) prevents input bleed from the selector
 * (e.g., Z press to launch immediately satisfying the gate).
 *
 * Determinism: the gate runs BEFORE the cartridge's GameContext is built,
 * so PRNG state isn't touched. Replay validation starts from the moment
 * the gate releases.
 */

import type { GameInstance, GameModule } from '@arcraz/arcadia-types';
import type { InputSource } from './loop.js';

const TITLE_YELLOW = '#ffea00';
const SUBTITLE_CYAN = '#00d9ff';
const FOOTER = '#7af3c8';

/** Window during which input is ignored after the gate paints its first
 *  frame. Without this, the same Z press that launched the cartridge from
 *  the selector would satisfy the gate immediately. */
const LOCKOUT_TICKS = 18; // ~300ms @ 60fps

export function runPressStart(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  module_: GameModule,
  instance: GameInstance
): Promise<void> {
  let elapsedTicks = 0;
  let blink = 0;

  return new Promise<void>((resolve) => {
    const tick = (): void => {
      const state = input.snapshot();
      elapsedTicks += 1;

      if (elapsedTicks > LOCKOUT_TICKS) {
        if (
          state.justPressed.has('start') ||
          state.justPressed.has('fireA') ||
          state.justPressed.has('coin')
        ) {
          resolve();
          return;
        }
      }

      // Re-render the cartridge's idle frame each tick (cheap — no
      // update() called, so game state is frozen at tick 0). Then paint
      // the press-start overlay on top.
      instance.render(ctx2d);
      blink = (blink + 1) % 60;
      render(ctx2d, module_, blink);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function render(
  g: CanvasRenderingContext2D,
  module_: GameModule,
  blink: number
): void {
  // Pull live canvas dimensions — cartridges declare their own logical
  // resolution (480x270 for pixel art, 960x540 for vector). The launcher
  // resizes the backing store before this overlay runs, so the values
  // here reflect the active cartridge.
  const WIDTH = g.canvas.width;
  const HEIGHT = g.canvas.height;
  // Type scale: 480x270 is the cabinet baseline; bigger canvases get
  // proportionally bigger overlay text so titles read at the same
  // perceived size after CSS letterbox-scales the canvas.
  const scale = HEIGHT / 270;

  // Scrim: darken the cartridge's initial frame so the overlay text reads
  // cleanly without obliterating the game pixels behind. ~55% opacity
  // black gradient — top half darker (where title sits) than bottom.
  const scrim = g.createLinearGradient(0, 0, 0, HEIGHT);
  scrim.addColorStop(0, 'rgba(4, 6, 13, 0.78)');
  scrim.addColorStop(0.55, 'rgba(4, 6, 13, 0.45)');
  scrim.addColorStop(1, 'rgba(4, 6, 13, 0.78)');
  g.fillStyle = scrim;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Cartridge title in its accent color — pulls from paletteHint[0].
  const accent = module_.paletteHint[0] ?? TITLE_YELLOW;
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.font = `bold ${Math.round(24 * scale)}px ui-monospace, monospace`;
  g.fillStyle = accent;
  g.fillText(module_.title, WIDTH / 2, 50 * scale);

  // Manufacturer plate underneath.
  g.font = `${Math.round(8 * scale)}px ui-monospace, monospace`;
  g.fillStyle = SUBTITLE_CYAN;
  g.fillText(module_.manufacturer, WIDTH / 2, 82 * scale);
  g.fillText(`${module_.genre.toUpperCase()} · ${module_.year}`, WIDTH / 2, 94 * scale);

  // Palette dot row — cartridge's signature colors as 8×8 squares.
  // Compact identity beat; doubles as a marquee-tint preview.
  const dotW = 8 * scale;
  const dotGap = 4 * scale;
  const dots = Math.min(module_.paletteHint.length, 6);
  const totalDotW = dots * dotW + (dots - 1) * dotGap;
  const dotStart = WIDTH / 2 - totalDotW / 2;
  for (let i = 0; i < dots; i++) {
    g.fillStyle = module_.paletteHint[i] as string;
    g.fillRect(dotStart + i * (dotW + dotGap), 116 * scale, dotW, dotW);
  }

  // PRESS START — blinks at 1Hz to match the cabinet's INSERT COIN aesthetic.
  if (blink < 40) {
    g.font = `bold ${Math.round(16 * scale)}px ui-monospace, monospace`;
    g.fillStyle = TITLE_YELLOW;
    g.fillText('PRESS START', WIDTH / 2, HEIGHT - 60 * scale);
  }

  // Footer hint — quieter, doesn't blink.
  g.font = `${Math.round(8 * scale)}px ui-monospace, monospace`;
  g.fillStyle = FOOTER;
  g.fillText('Z / ENTER / SPACE', WIDTH / 2, HEIGHT - 36 * scale);

  // Reset alignment defaults; cartridge code assumes 'left' / 'top'.
  g.textAlign = 'left';
  g.textBaseline = 'top';
}
